"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Sparkles, Zap, Eye, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const SIMULATOR_SHOWCASE = [
  {
    name: "Sports Car",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "sim-1",
        name: "Simulation A",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "sim-2",
        name: "Simulation B",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "sim-3",
        name: "Simulation C",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
  {
    name: "Sedan",
    before: "/showcase/images/before1.jpg",
    imageClassName: "object-[center_70%]",
    wheels: [
      {
        id: "sim-4",
        name: "Simulation A",
        image: "/showcase/images/after11.webp",
        thumbnail: "/showcase/images/after11.webp",
      },
      {
        id: "sim-5",
        name: "Simulation B",
        image: "/showcase/images/after12.webp",
        thumbnail: "/showcase/images/after12.webp",
      },
      {
        id: "sim-6",
        name: "Simulation C",
        image: "/showcase/images/after13.webp",
        thumbnail: "/showcase/images/after13.webp",
      },
    ],
  },
];

const SIMULATION_FEATURES = [
  {
    icon: <Zap className="h-5 w-5" />,
    title: "Real-time accuracy",
    description: "AI analyzes your car's angle, lighting, and stance to simulate wheels with photorealistic precision.",
  },
  {
    icon: <Eye className="h-5 w-5" />,
    title: "True-to-life rendering",
    description: "Shadows, reflections, and brake visibility are all simulated. Not a generic overlay-a realistic preview.",
  },
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: "Any wheel, any car",
    description: "Upload your own wheel images or choose from our library. The simulator adapts to any combination.",
  },
];

const SIMULATOR_FAQS = [
  {
    question: "How accurate is this wheel simulator?",
    answer:
      "Our simulator uses AI trained specifically for automotive wheel visualization. It considers perspective, lighting, reflections, and shadows to create simulations that look like real photos-not obvious mockups.",
  },
  {
    question: "Is this different from other wheel simulators?",
    answer:
      "Most wheel simulators use your car from their limited car library. We use YOUR actual car photo, so the simulation matches your exact color, trim, and condition. Plus, you can upload any wheel-not just what's in a catalog.",
  },
  {
    question: "Can I simulate wheels before buying?",
    answer:
      "That's exactly what it's for. Run simulations with different wheels, compare the results, and make your decision with visual proof. No more imagining or guessing.",
  },
  {
    question: "How fast are simulations generated?",
    answer:
      "Most simulations complete in under 60 seconds. Upload your photo, select a wheel, and see the result almost immediately.",
  },
  {
    question: "Can I share my simulations?",
    answer:
      "Yes. Download HD images of any simulation-no watermarks. Share with friends, post on forums, or bring to your wheel shop.",
  },
];

export function WheelSimulatorContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Wheel Simulator",
    url: "https://thewheelvisualizer.com/wheel-simulator",
    description:
      "Simulate how any wheel will look on your car with AI-powered accuracy. Realistic wheel simulations in seconds.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
      applicationSubCategory: "Wheel Simulator",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to simulate wheels on your car",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload your car photo",
        text: "Take a photo of your car and upload it to the wheel simulator.",
      },
      {
        "@type": "HowToStep",
        name: "Choose a wheel",
        text: "Select from our wheel library or upload your own wheel image.",
      },
      {
        "@type": "HowToStep",
        name: "Run simulation",
        text: "AI generates a realistic simulation showing how the wheel looks on your car.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Sparkles className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">AI-powered simulation</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Wheel Simulator
                <br />
                <span className="text-white/70">See Before You Buy</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Upload your car photo and simulate any wheel with AI-powered accuracy. Realistic previews that help you make confident decisions.
              </p>
              <p className="text-white/80 font-medium mb-8">
                Not a basic overlay-a true simulation of how wheels will look.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">AI-Powered</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">&lt;60s</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">HD Output</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/after3.webp"
                  alt="Wheel simulation result"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Photorealistic results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How simulation works */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The technology</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Not just an overlay
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              Generic simulators paste a wheel on your photo. Ours analyzes the entire scene-perspective, lighting, shadows-and generates a result that looks real.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {SIMULATION_FEATURES.map((feature) => (
              <div key={feature.title} className="group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70 group-hover:text-white group-hover:border-white/20 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison: Generic vs Ours */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The difference</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Why simulations look better here
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/40 mt-2.5 shrink-0" />
                  <div>
                    <div className="text-white font-medium mb-1">Your actual car, not a stock photo</div>
                    <p className="text-white/60 text-sm">Upload your car in its real environment with your paint, trim, and stance.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/40 mt-2.5 shrink-0" />
                  <div>
                    <div className="text-white font-medium mb-1">Lighting that matches</div>
                    <p className="text-white/60 text-sm">Simulated wheels adopt the same lighting as your photo-no flat, pasted-on look.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/40 mt-2.5 shrink-0" />
                  <div>
                    <div className="text-white font-medium mb-1">Any wheel from anywhere</div>
                    <p className="text-white/60 text-sm">Not limited to a preset catalog. Upload wheels from eBay, forums, or manufacturer sites.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/after2.webp"
                alt="Wheel simulation example"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-white/90 text-black text-sm font-medium">
                Simulated result
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive showcase */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Try it yourself</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See simulations in action
            </h2>
            <p className="text-white/60 mt-4">
              Drag the slider to compare. Tap thumbnails to switch wheel simulations.
            </p>
          </div>

          <div className="space-y-16">
            {SIMULATOR_SHOWCASE.map((scenario, idx) => (
              <div key={scenario.name}>
                <InteractiveShowcase
                  beforeImage={scenario.before}
                  wheels={scenario.wheels}
                  imageClassName={scenario.imageClassName}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Wheel simulation questions
            </h2>
            <p className="text-white/60 mt-4">
              What users ask about our wheel simulator.
            </p>
          </div>
          <FAQSection faqs={SIMULATOR_FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
